/* eslint-disable react/jsx-filename-extension */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Tooltip, Spin, Row,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import { Files } from 'ontraccr-common';
import FilePreview from './FilePreview';

import { getCroppedDimensions } from '../../helpers/photos';

import InvisibleButton from '../buttons/InvisibleButton';
import ProgressWheel from '../ProgressWheel';

import { visuallyDownloadFile } from '../../files/fileHelpers';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const IMAGE_DIM = 100;
const SMALL_DIM = 50;

const getTimeText = (ts) => (
  DateTime
    .fromMillis(ts)
    .toLocaleString(DateTime.DATETIME_MED)
);

export default function PhotoReelPreview({
  rotation = 0,
  uri,
  file,
  type,
  shadow,
  onDelete,
  onClick,
  small,
  showCondensedView,
  locationMap = {},
  timestamp,
  onImageLoad: onImageLoadProp,
  thumbnailMap = {},
}) {
  const dispatch = useDispatch();

  const cursor = onClick ? 'pointer' : 'auto';
  const ourDim = small ? SMALL_DIM : IMAGE_DIM;
  const globalFileMap = useSelector((state) => state.files.fileMap);
  const [style, setStyle] = useState({ });
  const [loaded, setLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasError, setHasError] = useState(false);

  const loading = progress > 0 && progress < 100;

  const trueId = file?.id || file?.autoSaveId;
  const globalFile = file instanceof File ? file : globalFileMap[trueId];

  const thumbnailURL = thumbnailMap[trueId] ?? file?.thumbnailURL;

  useEffect(() => {
    setLoaded(false);
  }, [file, uri]);

  useEffect(() => {
    setHasError(false);
  }, [thumbnailURL]);

  const onImageLoad = ({ target: img }) => {
    if (!loaded) {
      setLoaded(true);
      setStyle(getCroppedDimensions({
        img,
        scale: 8,
        height: ourDim,
        width: ourDim,
      }));
      if (onImageLoadProp) onImageLoadProp(trueId);
    }
  };

  const updateTimeText = useMemo(() => (
    timestamp
      ? getTimeText(timestamp)
      : null
  ), [timestamp]);

  const getLocations = (id) => {
    let locationString = '';
    if (!locationMap[id]) return '';
    const locationArr = locationMap[id].map((obj) => {
      const location = obj?.location;
      const time = obj.timestamp ? `at ${getTimeText(obj.timestamp)}` : '';
      return `${location} ${time}`;
    });
    locationString = locationArr.join(', ');
    return `Location(s): ${locationString}`;
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!onClick) return;
    if (!globalFile || !(globalFile instanceof File)) {
      visuallyDownloadFile({
        dispatch,
        setProgress,
        file,
      }).then((success) => {
        if (success) {
          onClick();
        }
      });
      return;
    }
    onClick();
  };

  const getChildView = () => {
    if (loading) {
      return <ProgressWheel percentage={progress} style={{ position: 'absolute' }} />;
    }

    let showPreview = false;
    if ((!globalFile || !(globalFile instanceof File)) && !thumbnailURL) showPreview = true;
    else if (globalFile && globalFile instanceof File) {
      const { size } = globalFile;
      if (size > MAX_FILE_SIZE) showPreview = true;
    }

    if (showPreview) {
      return (
        <FilePreview
          file={file}
          onClick={handleClick}
          small={small}
          location={getLocations(file?.id)}
          style={{
            paddingTop: 10,
          }}
        />
      );
    }

    let fileType = globalFile?.type ?? file?.type ?? file?.jsFileObject?.type;

    if (!fileType && file?.name) {
      fileType = Files.getFileType(file);
    }

    switch (fileType) {
      case 'image':
      case 'image/png':
      case 'image/jpeg':
      case 'image/svg+xml': {
        if (hasError) {
          return (
            <Row justify="center" align="middle" style={{ height: '100%', width: '100%', paddingTop: 5 }}>
              <Spin />
            </Row>
          );
        }
        const src = (globalFile && globalFile instanceof File && !thumbnailURL)
          ? URL.createObjectURL(globalFile)
          : thumbnailURL;
        return (
          <Tooltip placement="bottom" title={getLocations(file?.id)}>
            <InvisibleButton
              onClick={handleClick}
              style={{
                height: '100%',
              }}
            >
              <img
                className={small ? 'photo-preview-small' : 'photo-preview'}
                src={src}
                key={file?.id}
                alt="Post"
                onLoad={onImageLoad}
                onError={() => setHasError(true)}
                style={{
                  ...style,
                  cursor,
                  width: '100%',
                  height: '100%',
                  transform: `rotate(${rotation}deg)`,
                  top: 0,
                  objectFit: 'cover',
                }}
              />
            </InvisibleButton>
          </Tooltip>
        );
      }
      // TODO - do we want to do video thumbnail?
      default:
        return (
          <FilePreview
            file={file}
            onClick={handleClick}
            small={small}
            location={getLocations(file?.id)}
            style={{
              paddingTop: 10,
            }}
          />
        );
    }
  };

  const width = showCondensedView ? ourDim : ourDim + 10;
  const height = showCondensedView ? ourDim : ourDim + 20;
  return (
    <div
      key={uri}
      style={{
        height: height + (timestamp ? 40 : 0),
        width,
        borderRadius: 4,
      }}
    >
      <div
        className={`photo-preview-container ${shadow ? 'photo-shadow' : ''}`}
        style={{
          height: ourDim,
          width: ourDim,
        }}
      >
        {uri && type === 'image' ? (
          <InvisibleButton
            onClick={onClick}
            style={{
              height: '100%',
            }}
          >
            <img
              className={small ? 'photo-preview-small' : 'photo-preview'}
              src={globalFile instanceof File ? URL.createObjectURL(globalFile) : uri}
              key={uri}
              alt="Post"
              onLoad={onImageLoad}
              style={{
                ...style,
                cursor,
              }}
            />
          </InvisibleButton>
        ) : getChildView()}
      </div>
      {
        updateTimeText
        && (
          <div style={{ width, fontSize: 10, textAlign: 'center' }}>
            {updateTimeText}
          </div>
        )
      }
      {onDelete && (
      <Button
        className="photo-preview-delete"
        icon={
          <CloseOutlined className="photo-preview-delete-icon" />
        }
        onClick={onDelete}
      />
      )}
    </div>
  );
}

PhotoReelPreview.propTypes = {
  showCondensedView: PropTypes.bool,
  shadow: PropTypes.bool,
  small: PropTypes.bool,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  file: PropTypes.shape({
    id: PropTypes.string,
    autoSaveId: PropTypes.string,
    type: PropTypes.string,
    url: PropTypes.string,
    name: PropTypes.string,
    thumbnailURL: PropTypes.string,
    jsFileObject: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
  uri: PropTypes.string,
  type: PropTypes.string,
  locationMap: PropTypes.shape({}),
  timestamp: PropTypes.number,
  rotation: PropTypes.number,
  onImageLoad: PropTypes.func,
  thumbnailMap: PropTypes.shape({}),
};

PhotoReelPreview.defaultProps = {
  showCondensedView: false,
  shadow: true,
  small: false,
  onClick: null,
  onDelete: null,
  file: null,
  uri: null,
  type: null,
  locationMap: {},
  timestamp: null,
  rotation: 0,
  onImageLoad: null,
  thumbnailMap: {},
};
